// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import dynamic from "next/dynamic"
import { useEffect, useRef, useState } from "react"
import type { LexicalEditor } from "lexical"
import type { CmsProvenance } from "../provenance"
import type { LexicalContent } from "@/lib/cms"
import { ModalShell } from "../modal-shell"
import { cmsFieldLabel } from "@/lib/edit/editable-cms-fields"
import {
  decideLexicalGuard,
  decideLexicalLoad,
  cmsAdminDocUrl,
} from "./lexical-body-guard"

/**
 * Whole-field Lexical editor modal SHELL for a Payload rich-text body
 * (AHD-7515). The shell is statically importable (it's already behind the
 * render-gated editor tree), but the actual editor — which pulls in `lexical` +
 * `@lexical/*` — is loaded via `next/dynamic({ ssr: false })` so that bundle
 * never ships to public visitors and only downloads when this modal opens.
 *
 * Flow: fetch the field's draft tree → `decideLexicalLoad` (null/empty → seed an
 * empty editable doc; malformed → fail loud "edit in the CMS"; unsupported block
 * → fail loud; valid → ready) → mount the editor → on Save, serialize the LIVE
 * editor state (not the debounced copy), re-guard it, and hand it to `onSave`
 * with the `baseline` draft the editor opened on so the apply can refuse a
 * lost-update overwrite.
 */
const Editor = dynamic(
  () => import("./lexical-body-editor").then((m) => m.LexicalBodyEditor),
  {
    ssr: false,
    loading: () => <div style={loadingBox}>Loading editor…</div>,
  }
)

type LoadState =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "unsupported"; types: string[] }
  | { phase: "malformed" }
  | { phase: "ready"; content: LexicalContent }

export function LexicalBodyModal({
  provenance,
  idToken,
  onSave,
  onClose,
  /** When reopening a staged cart item: the in-progress tree to seed the editor
   *  with instead of refetching the draft. Skips the network and shows the user's
   *  own edit. The baseline still comes from the fetched draft so the lost-update
   *  guard compares against the live CMS state, not the staged edit. */
  initialTreeOverride,
  /** Override for the Payload admin doc URL when it can't be derived from
   *  NEXT_PUBLIC_CMS_API_URL (host passes it). */
  adminUrlOverride,
}: {
  provenance: CmsProvenance
  idToken: string
  /** Hands up the edited tree + the baseline draft it was edited from. */
  onSave: (content: LexicalContent, baseline: LexicalContent) => void
  onClose: () => void
  initialTreeOverride?: LexicalContent
  adminUrlOverride?: string
}) {
  const { collection, id, field, locale } = provenance
  const [state, setState] = useState<LoadState>({ phase: "loading" })
  // The fetched draft tree the editor opened on — the lost-update baseline the
  // apply re-fetches and compares against before writing.
  const baselineTree = useRef<LexicalContent | null>(null)
  // The live editor instance, captured on mount. Save serializes its CURRENT
  // state so a final keystroke can't be lost to the debounced onChange.
  const editorRef = useRef<LexicalEditor | null>(null)
  // The tree the editor was seeded with, to detect unsaved changes on close.
  const seededTree = useRef<string | null>(null)
  const [dirty, setDirty] = useState(false)
  // Shows the in-editor "discard unsaved changes?" confirm instead of a native
  // window.confirm (which would freeze the bridge). (finding 8)
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  const adminUrl =
    adminUrlOverride ??
    cmsAdminDocUrl(process.env.NEXT_PUBLIC_CMS_API_URL, collection, id) ??
    undefined

  // Escape is handled by ModalShell's single capture-phase listener — no second
  // listener here.

  // Fetch the field's current draft tree. It's both the editor seed (unless a
  // staged-edit override is supplied on reopen) and the lost-update baseline the
  // apply compares against. `decideLexicalLoad` decides: null/empty → seed an
  // empty editable doc; malformed → fail loud; valid → ready (after the
  // unsupported-node screen).
  useEffect(() => {
    let cancelled = false
    const qs = new URLSearchParams({ collection, id, field, locale })
    fetch(`/api/edit/cms-field?${qs}`, {
      headers: { "User-Authorization": `Bearer ${idToken}` },
    })
      .then(async (res) => {
        if (!res.ok)
          throw new Error(`Couldn’t load this article (${res.status}).`)
        const { value } = (await res.json()) as { value: unknown }
        const decision = decideLexicalLoad(value)
        if (cancelled) return
        if (decision.kind === "malformed") {
          setState({ phase: "malformed" })
          return
        }
        if (decision.kind === "unsupported") {
          setState({ phase: "unsupported", types: decision.unsupported })
          return
        }
        // Baseline is always the fetched draft (the seed for the guard);
        // `initialTreeOverride` only changes what the user edits, never the
        // baseline the apply guards against.
        baselineTree.current = decision.tree
        const seed = initialTreeOverride ?? decision.tree
        seededTree.current = JSON.stringify(seed)
        setState({ phase: "ready", content: seed })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState({
          phase: "error",
          message:
            err instanceof Error ? err.message : "Couldn’t load this article.",
        })
      })
    return () => {
      cancelled = true
    }
  }, [collection, id, field, locale, idToken, initialTreeOverride])

  function handleSave() {
    const editor = editorRef.current
    const baseline = baselineTree.current
    if (!editor || !baseline) return
    // Serialize the LIVE editor state at click-time so a final keystroke typed
    // inside the debounce window isn't lost.
    const tree = editor.getEditorState().toJSON() as unknown as LexicalContent
    // Re-guard before staging — a save must never persist a tree with an
    // unsupported block (belt-and-suspenders alongside the load guard).
    if (decideLexicalGuard(tree).kind !== "ok") return
    onSave(tree, baseline)
    onClose()
  }

  // Guarded close: if the tree changed since open, surface the in-editor confirm
  // instead of silently discarding the edit. Save and the confirm's own
  // "Discard" bypass this (they close intentionally).
  function requestClose() {
    if (dirty) {
      setConfirmDiscard(true)
      return
    }
    onClose()
  }

  const humanLabel = cmsFieldLabel(collection, field)

  return (
    <ModalShell
      onClose={requestClose}
      ariaLabel={humanLabel}
      title={
        <div style={headerWrap}>
          <h3 style={humanHeader}>{humanLabel}</h3>
          <code style={idSubline}>{`${collection}:${id}:${field}`}</code>
        </div>
      }
      width={760}
      maxHeight="88vh"
    >
      <div style={draftBadge}>Saves as a draft — needs publishing.</div>
      <div style={localeRow}>Locale: {locale.toUpperCase()}</div>

      {state.phase === "loading" ? (
        <div style={loadingBox}>Loading article…</div>
      ) : null}

      {state.phase === "error" ? (
        <p style={errorText}>{state.message}</p>
      ) : null}

      {state.phase === "unsupported" ? (
        <div style={unsupportedBox}>
          <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
            This article contains a block type the inline editor can’t safely
            edit yet ({state.types.join(", ")}). Edit it in the CMS.
          </p>
          {adminUrl ? (
            <a href={adminUrl} target="_blank" rel="noopener" style={adminLink}>
              Open in the CMS →
            </a>
          ) : null}
        </div>
      ) : null}

      {state.phase === "malformed" ? (
        <div style={unsupportedBox}>
          <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
            This article body isn’t a rich-text tree the inline editor can open.
            Edit it in the CMS.
          </p>
          {adminUrl ? (
            <a href={adminUrl} target="_blank" rel="noopener" style={adminLink}>
              Open in the CMS →
            </a>
          ) : null}
        </div>
      ) : null}

      {state.phase === "ready" ? (
        <>
          <Editor
            initialContent={state.content}
            onEditorReady={(editor) => {
              editorRef.current = editor
            }}
            onTreeChange={(tree) => {
              // Dirty = the live tree differs from the seed. Cheap structural
              // compare via JSON; the editor debounces this signal.
              setDirty(JSON.stringify(tree) !== seededTree.current)
            }}
          />
          {confirmDiscard ? (
            <div style={confirmBar} role="alertdialog" aria-live="polite">
              <span style={{ flex: 1 }}>Discard your unsaved changes?</span>
              <button
                style={cancelBtn}
                onClick={() => setConfirmDiscard(false)}
              >
                Keep editing
              </button>
              <button style={discardBtn} onClick={onClose}>
                Discard
              </button>
            </div>
          ) : (
            <div style={actions}>
              <button style={cancelBtn} onClick={requestClose}>
                Cancel
              </button>
              <button style={saveBtn} onClick={handleSave}>
                Save
              </button>
            </div>
          )}
        </>
      ) : null}
    </ModalShell>
  )
}

const draftBadge: React.CSSProperties = {
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  color: "#2f6f3e",
  background: "#e7f6ec",
  borderRadius: 6,
  padding: "3px 8px",
  marginTop: -6,
  marginBottom: 10,
}
const headerWrap: React.CSSProperties = {
  margin: "0 0 10px",
}
const humanHeader: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: "#15151a",
  fontFamily: "ui-sans-serif, system-ui",
}
const idSubline: React.CSSProperties = {
  display: "block",
  marginTop: 3,
  fontSize: 11,
  color: "#9a9aa2",
  fontFamily: "ui-monospace, monospace",
  wordBreak: "break-all",
}
// Plain label, not a tab/button — the old blue styling read as an interactive
// locale switcher. (finding 8)
const localeRow: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#6b7280",
  marginBottom: 10,
}
const loadingBox: React.CSSProperties = {
  padding: "40px 0",
  textAlign: "center",
  color: "#888",
  fontSize: 13,
}
const errorText: React.CSSProperties = {
  color: "#b3261e",
  fontSize: 13,
}
const unsupportedBox: React.CSSProperties = {
  padding: 14,
  border: "1px solid #f0d8a8",
  background: "#fdf6e9",
  borderRadius: 10,
  fontSize: 13,
  color: "#5a4a22",
  lineHeight: 1.5,
}
const adminLink: React.CSSProperties = {
  color: "#2b6cff",
  fontWeight: 600,
  textDecoration: "none",
}
const actions: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 14,
}
const confirmBar: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 14,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #f0d8a8",
  background: "#fdf6e9",
  fontSize: 13,
  color: "#5a4a22",
}
const discardBtn: React.CSSProperties = {
  border: "none",
  borderRadius: 9,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  background: "#b3261e",
  color: "#fff",
}
const cancelBtn: React.CSSProperties = {
  border: "1px solid #dcdce0",
  background: "#fff",
  borderRadius: 9,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  color: "#444",
}
const saveBtn: React.CSSProperties = {
  border: "none",
  borderRadius: 9,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  background: "#2f6f3e",
  color: "#fff",
}
